import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getUserVerificationInfo } from '../requests';
import { useSessionStore } from '@/entities/session';
import { useEffect, useState } from 'react';
import {
  VERIFICATION_REFETCH_INTERVAL,
  VERIFICATION_STEPS,
} from '@/shared/config';

const useVerificationInfo = (isEnabled: boolean = true) => {
  const queryClient = useQueryClient();
  const { session, refreshSession } = useSessionStore();
  const [isRefreshingSession, setIsRefreshingSession] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['user-verification-info', session?.userId],
    queryFn: getUserVerificationInfo,
    enabled: isEnabled && !!session?.userId,
    refetchInterval: query => {
      const step = query.state.data?.stepId;

      if (
        step === VERIFICATION_STEPS.SUMSUB ||
        step === VERIFICATION_STEPS.COMPLIANCE_CHECK
      ) {
        return VERIFICATION_REFETCH_INTERVAL;
      }

      return false;
    },
  });

  useEffect(() => {
    if (!data) return;

    const alreadyVerified = session?.additional?.isVerified;
    const isCorporate = session?.additional?.isCorporate;
    const isVerificationStepIdFinish =
      data?.stepId === VERIFICATION_STEPS.FINISH;
    const isVerificationResultSuccess = data?.result === 'success';

    const isNeedToRefreshPersonalSession =
      !alreadyVerified &&
      isVerificationStepIdFinish &&
      isVerificationResultSuccess &&
      !isCorporate;

    if (isNeedToRefreshPersonalSession) {
      setIsRefreshingSession(true);

      refreshSession()
        .then(newSession => {
          if (newSession?.userId) {
            queryClient.invalidateQueries({
              queryKey: ['accounts-info', newSession.userId],
            });
            queryClient.invalidateQueries({
              queryKey: ['accounts-rests-in-base-asset', newSession.userId],
            });
          }
        })
        .finally(() => {
          setIsRefreshingSession(false);
        });
    }
  }, [
    data,
    isLoading,
    queryClient,
    session?.additional?.isVerified,
    session?.additional?.isCorporate,
  ]);

  return {
    refetchVerificationInfo: refetch,
    userVerificationInfo: data,
    isVerificationInfoLoading: isLoading || isRefreshingSession,
    userVerificationInfoError: error,
  };
};

export { useVerificationInfo };
